import React, { useState, ChangeEvent } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Button,
  Paper,
} from "@mui/material";
import { CloudUpload, Image, PictureAsPdf, Delete } from "@mui/icons-material";

interface FileUploaderProps {
  type: "image" | "pdf";
  setFile: (file: File | undefined) => void;
  file: File | undefined;
}

export const FileUploader: React.FC<FileUploaderProps> = ({ type, setFile, file }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      handleClose();
    }
  };

  const handleDeleteFile = () => {
    setFile(undefined);
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          p: .5,
          textAlign: "center",
          cursor: "pointer",
          "&:hover": {
            borderColor: "primary.main",
          },
          width: { xs: "100%", sm: 150 },
          maxWidth: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
        onClick={handleOpen}
      >
        {file && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteFile();
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              color: "error.main",
              backgroundColor: "background.paper",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        )}

        {(!file || type !== "image") && (
          <Box sx={{ 
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexDirection: { xs: "row", }, 
          }}>
            <IconButton
              color={file ? "primary" : "inherit"}
              sx={{ 
                opacity: file ? 1 : 0.5, 
                transition: "opacity 0.3s",
                padding: 0.5, 
                "& .MuiSvgIcon-root": {
                  fontSize: "1.5rem",
                }
              }}
            >
              {type === "image" ? <Image fontSize="inherit" /> : <PictureAsPdf fontSize="inherit" />}
            </IconButton>
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: "left" }}>
              {file ? `Archivo cargado: ${file.name}` : `Cargar ${type === "image" ? "Imagen" : "Documento"}`}
            </Typography>
          </Box>
        )}

        {type === "image" && file && (
          <img
            src={URL.createObjectURL(file)}
            alt="Vista previa"
            style={{
              maxHeight: "100px",
              // width: "100%",
              borderRadius: 4,
              border: "1px solid #ccc",
            }}
          />
        )}
      </Paper>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <CloudUpload fontSize="large" color="primary" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Selecciona un archivo
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {type === "image" ? "Formatos soportados: PNG, JPEG" : "Formato soportado: PDF"}
          </Typography>
          <input
            type="file"
            accept={type === "image" ? "image/*" : "application/pdf"}
            onChange={handleFileChange}
            style={{ display: "none" }}
            id={`file-upload-${type}`}
          />
          <label htmlFor={`file-upload-${type}`}>
            <Button variant="contained" component="span">
              Seleccionar archivo
            </Button>
          </label>
        </Box>
      </Modal>
    </>
  );
};